import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Check } from "../components/icons";
import type { Product } from "../types";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./LanguageContext";

const STORAGE_KEY = "barcraft.rfq.items";
const TOAST_DURATION_MS = 2800;

export type CartItem = Product & { quantity: number };

interface RFQContextValue {
  items: CartItem[];
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  addItem: (product: Product) => void;
  removeItem: (productId: string) => void;
  toggleItem: (product: Product) => void;
  setQuantity: (productId: string, quantity: number) => void;
  isSelected: (productId: string) => boolean;
  clearItems: () => void;
}

const RFQContext = createContext<RFQContextValue | null>(null);

function readStoredItems(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

export function RFQProvider({ children }: { children: ReactNode }) {
  const { t } = useLanguage();
  const { user, isLoading: isAuthLoading } = useAuth();
  const [items, setItems] = useState<CartItem[]>(readStoredItems);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<{ key: number; name: string; image: string } | null>(null);
  const toastTimer = useRef<number>();

  const showAddedToast = (product: Product) => {
    window.clearTimeout(toastTimer.current);
    setToast({ key: Date.now(), name: product.name, image: product.image });
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
  };
  const syncedForUser = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        // Ignore localStorage write errors
      }
    }
  }, [items, user]);

  // On login (including an already-logged-in session resuming on page load), merge any
  // guest-cart items into the account's server cart, then treat the server as the source of truth.
  useEffect(() => {
    if (isAuthLoading || !user) return;
    if (syncedForUser.current === user.name) return;
    syncedForUser.current = user.name;

    const guestItems = readStoredItems();

    (async () => {
      try {
        for (const item of guestItems) {
          for (let i = 0; i < item.quantity; i++) {
            await fetch(`/api/cart/${item.id}`, { method: "POST" }).catch(() => null);
          }
        }
      } catch (e) {
        // ignore errors during sync
      }
      if (guestItems.length > 0) {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {}
      }
      try {
        const res = await fetch("/api/cart");
        if (res.ok) setItems(await res.json());
      } catch (e) {
        // ignore
      }
    })();
  }, [user, isAuthLoading]);

  useEffect(() => {
    if (!user) {
      syncedForUser.current = null;
      setItems(readStoredItems());
    }
  }, [user]);

  const value = useMemo<RFQContextValue>(
    () => ({
      items,
      isDrawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
      addItem: (product) => {
        showAddedToast(product);
        if (user) {
          fetch(`/api/cart/${product.id}`, { method: "POST" })
            .then((res) => res.json())
            .then(setItems)
            .catch(() => {}); // silent fail
          return;
        }
        setItems((prev) => {
          const existing = (prev || []).find((item) => item.id === product.id);
          if (existing) {
            return (prev || []).map((item) =>
              item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
            );
          }
          return [...(prev || []), { ...product, quantity: 1 }];
        });
      },
      removeItem: (productId) => {
        if (user) {
          fetch(`/api/cart/${productId}`, { method: "DELETE" })
            .then((res) => res.json())
            .then(setItems)
            .catch(() => {});
          return;
        }
        setItems((prev) => (prev || []).filter((item) => item.id !== productId));
      },
      toggleItem: (product) => {
        const alreadyAdded = (items || []).some((item) => item.id === product.id);
        if (!alreadyAdded) showAddedToast(product);
        if (user) {
          fetch(`/api/cart/${product.id}`, { method: alreadyAdded ? "DELETE" : "POST" })
            .then((res) => res.json())
            .then(setItems)
            .catch(() => {});
          return;
        }
        setItems((prev) =>
          alreadyAdded
            ? (prev || []).filter((item) => item.id !== product.id)
            : [...(prev || []), { ...product, quantity: 1 }]
        );
      },
      setQuantity: (productId, quantity) => {
        if (user) {
          fetch(`/api/cart/${productId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ quantity }),
          })
            .then((res) => res.json())
            .then(setItems)
            .catch(() => {});
          return;
        }
        setItems((prev) =>
          quantity <= 0
            ? (prev || []).filter((item) => item.id !== productId)
            : (prev || []).map((item) => (item.id === productId ? { ...item, quantity } : item))
        );
      },
      isSelected: (productId) => (items || []).some((item) => item.id === productId),
      clearItems: () => {
        if (user) {
          fetch("/api/cart", { method: "DELETE" }).then(() => setItems([])).catch(() => {});
          return;
        }
        setItems([]);
      },
    }),
    [items, isDrawerOpen, user]
  );

  return (
    <RFQContext.Provider value={value}>
      {children}
      {toast && (
        <div
          key={toast.key}
          role="status"
          className="animate-toastIn fixed inset-x-4 bottom-6 z-[80] flex justify-center sm:inset-x-auto sm:right-6 sm:justify-start"
        >
          <div className="relative flex w-full max-w-sm items-center gap-3 overflow-hidden border border-steel-200 bg-white py-3 pl-3.5 pr-4 shadow-lg">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden border border-steel-200 bg-white">
              <img src={toast.image} alt="" draggable={false} className="h-full w-full object-contain" />
            </span>
            <p className="min-w-0 text-sm leading-snug">
              <span className="line-clamp-2 font-semibold text-ink">{toast.name}</span>
              <span className="mt-0.5 flex items-center gap-1.5 text-xs text-steel-500">
                <Check size={13} className="shrink-0 text-accent" />
                {t("rfqToastAdded")}
              </span>
            </p>
            <div
              key={toast.key}
              className="animate-toastTimer absolute inset-x-0 bottom-0 h-0.5 origin-left bg-accent"
            />
          </div>
        </div>
      )}
    </RFQContext.Provider>
  );
}

export function useRFQ() {
  const ctx = useContext(RFQContext);
  if (!ctx) throw new Error("useRFQ must be used within an RFQProvider");
  return ctx;
}
